import { useState } from 'preact/hooks';
import type { PhoneApp } from '../app/phone.js';
import type { LineView } from '../sip/lines.js';
import { formatClock } from '../keepalive/timers.js';
import { mosBand } from '../media/mos.js';
import { formatAuNumber } from './format.js';
import { useTick } from './cards.js';

type DialogKind = 'transfer.blind' | 'consult' | 'add' | null;

const DTMF_KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9', '*', '0', '#'] as const;

function TargetDialog({ title, action, onSubmit, onCancel }: { title: string; action: string; onSubmit: (target: string) => void; onCancel: () => void }) {
  const [v, setV] = useState('');
  return (
    <form
      class="dialog"
      onSubmit={(e) => {
        e.preventDefault();
        if (v.trim()) onSubmit(v.trim());
      }}
    >
      <label>
        {title}
        <input autoFocus data-testid="target-input" value={v} onInput={(e) => setV((e.target as HTMLInputElement).value)} placeholder="Extension or number" inputMode="tel" />
      </label>
      <div class="row">
        <button type="submit" class="primary" data-testid="target-submit">
          {action}
        </button>
        <button type="button" onClick={onCancel}>
          Cancel
        </button>
      </div>
    </form>
  );
}

export function CallCard({ app, line }: { app: PhoneApp; line: LineView }) {
  useTick();
  const [dialog, setDialog] = useState<DialogKind>(null);
  const [dtmfOpen, setDtmfOpen] = useState(false);
  const call = line.callId ? app.store.state.value.calls[line.callId] : undefined;
  const pending = app.pendingKinds.value;
  const now = app.clock.now();
  const duration = line.answeredAt ? formatClock(now - line.answeredAt) : '';
  const heldFor = line.heldAt ? formatClock(now - line.heldAt) : '';
  const remoteHeld = call?.state === 'held' && call.held_by === 'remote';
  const who = line.remoteName || call?.remote_name || formatAuNumber(line.remoteNumber || call?.remote_number) || 'Unknown';
  const cls = ['card', line.lost || line.state === 'ended' ? 'lost' : '', line.held || remoteHeld ? 'held' : ''].join(' ');
  const spin = (k: string) => (pending.has(k) ? <span class="spinner" aria-label="Working" /> : null);

  if (line.state === 'ended') {
    const reasons: Record<string, string> = {
      media_lost: 'Call lost — phone server unreachable',
      ice_failed: 'Call failed — media could not connect',
      server_restart: 'Call ended — server restart',
      remote_hangup: 'Call ended',
      hangup: 'Call ended',
      declined: 'Declined',
      cancelled: 'Caller hung up',
      missed: 'Missed',
      busy: 'Busy',
      failed: 'Call failed',
    };
    return (
      <section class={cls} role="status" data-testid="call-ended" data-line={line.id} data-call-id={line.callId ?? ''} data-reason={line.endedReason ?? ''}>
        <div class="who">{reasons[line.endedReason ?? ''] ?? `Call ended — ${line.endedReason ?? ''}`}</div>
        <div class="sub">
          {who}
          {duration ? ` · ${duration}` : ''}
        </div>
      </section>
    );
  }

  return (
    <section
      class={cls}
      aria-label={`Call with ${who}`}
      data-testid="call-card"
      data-line={line.id}
      data-call-id={line.callId ?? ''}
      data-line-state={line.state}
      data-call-state={call?.state ?? ''}
      data-held={line.held ? 'true' : 'false'}
      data-conference={call?.conference ? 'true' : 'false'}
      data-roster={String(call?.roster.length ?? 0)}
    >
      <div class="row">
        <div style={{ flex: 1 }}>
          <div class="who">{who}</div>
          <div class="sub">
            {line.state === 'dialing' ? 'Calling…' : line.state === 'early' ? 'Ringing…' : duration}
            {line.held ? ` · on hold ${heldFor}` : remoteHeld ? ' · held by the other party' : ''}
            {call?.queue_name ? ` · ${call.queue_name}` : ''}
            {call?.conference ? ` · conference (${call.roster.length})` : ''}
            {call?.consult ? ` · consulting ${call.consult.target}` : ''}
          </div>
        </div>
        {line.viaTurn ? <span class="chip turn">via TURN</span> : null}
        {line.stats ? (
          <span class={`mos ${mosBand(line.stats.mos)}`} title={`jitter ${line.stats.jitterMs} ms · loss ${line.stats.lossPct}% · rtt ${line.stats.rttMs} ms · ${line.stats.codec}`}>
            MOS {line.stats.mos.toFixed(1)}
          </span>
        ) : null}
      </div>

      {line.state === 'up' ? (
        <div class="row">
          <button data-testid="call-hold" aria-pressed={line.held} onClick={() => void app.hold(line.id, !line.held)} aria-keyshortcuts="Control+Shift+H">
            {line.held ? 'Resume' : 'Hold'} {spin(line.held ? 'unhold' : 'hold')}
          </button>
          <button data-testid="call-mute" aria-pressed={line.muted} class={line.muted ? 'danger' : ''} onClick={() => void app.mute(line.id, !line.muted)} aria-keyshortcuts="Control+Shift+M">
            {line.muted ? 'Unmute' : 'Mute'} {spin('mute')}
          </button>
          <button aria-expanded={dtmfOpen} onClick={() => setDtmfOpen(!dtmfOpen)}>
            Keypad
          </button>
          {line.callId ? (
            <>
              <button data-testid="call-transfer" onClick={() => setDialog('transfer.blind')}>Transfer {spin('transfer.blind')}</button>
              {call?.consult ? (
                <>
                  <button data-testid="call-complete-transfer" onClick={() => void app.consultComplete(line.callId!)}>Complete transfer {spin('transfer.attended.complete')}</button>
                  <button data-testid="call-swap" onClick={() => void app.swap(line.callId!)}>Swap {spin('swap')}</button>
                  <button data-testid="call-merge" onClick={() => void app.merge(line.callId!)}>Merge {spin('conf.merge')}</button>
                </>
              ) : (
                <button data-testid="call-consult" onClick={() => setDialog('consult')}>Consult {spin('transfer.attended.start')}</button>
              )}
              <button data-testid="call-add-party" onClick={() => setDialog('add')} disabled={(call?.roster.length ?? 0) >= app.launch.policy.maxConfParties}>
                Add party {spin('conf.add')}
              </button>
            </>
          ) : null}
        </div>
      ) : null}

      {dtmfOpen && line.state === 'up' ? (
        <div class="dtmf" role="group" aria-label="Keypad">
          {DTMF_KEYS.map((k) => (
            <button key={k} onClick={() => app.dtmf(line.id, k)}>
              {k}
            </button>
          ))}
        </div>
      ) : null}

      {dialog === 'transfer.blind' ? (
        <TargetDialog title="Transfer to" action="Transfer" onCancel={() => setDialog(null)} onSubmit={(t) => { setDialog(null); void app.transferBlind(line.callId!, t); }} />
      ) : null}
      {dialog === 'consult' ? (
        <TargetDialog title="Consult with (attended transfer)" action="Start consult" onCancel={() => setDialog(null)} onSubmit={(t) => { setDialog(null); void app.consultStart(line.callId!, t); }} />
      ) : null}
      {dialog === 'add' ? (
        <TargetDialog title="Add party" action="Add" onCancel={() => setDialog(null)} onSubmit={(t) => { setDialog(null); void app.addParty(line.callId!, t); }} />
      ) : null}

      {call && call.roster.length > 0 ? (
        <ul class="roster" aria-label="Participants">
          {call.roster.map((r) => (
            <li key={r.channel_id} data-testid="roster-entry" data-kind={r.kind} data-state={r.state}>
              <span class={r.talking ? 'talking' : ''}>{r.label}</span>
              {r.number ? <span class="sub">{formatAuNumber(r.number)}</span> : null}
              <span class="sub">{r.state}</span>
              {r.kind !== 'customer' || call.conference ? (
                <>
                  <button class="ghost" aria-pressed={r.muted} onClick={() => void app.partyMute(call.call_id, r.channel_id, !r.muted)}>
                    {r.muted ? 'Unmute' : 'Mute'} {spin('conf.mute')}
                  </button>
                  <button class="ghost" onClick={() => void app.partyDrop(call.call_id, r.channel_id)}>
                    Drop {spin('conf.drop')}
                  </button>
                </>
              ) : null}
            </li>
          ))}
        </ul>
      ) : null}

      <div class="row">
        <button class="danger" data-testid="call-hangup" onClick={() => void app.hangup(line.id)}>
          {call?.conference ? (app.launch.policy.leaveConference === 'end_all' ? 'End conference' : 'Leave') : 'Hang up'} {spin('hangup')}
        </button>
      </div>
    </section>
  );
}
